import { LanguagesEnum } from '@/api/enums';
import { PhishingSign } from '@/client/campaigns';
import { phishingContentLanguages } from '@/common/constants/languages';
import { isEmptyRichText } from '@/utils/helpers/rich-text.helper';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import styled from 'styled-components';
import { PhishingSignCard } from './PhishingSignCard';
import { PhishingSignIcon } from './PhishingSignIcon';

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: var(--big-padding);
`;

const StyledAccordion = styled(Accordion)`
  &.p-accordion {
    display: flex;
    flex-direction: column;
    gap: var(--default-padding);

    .p-accordion-tab {
      background-color: white;
      border: 1px solid var(--gray-dark);
      border-radius: var(--semi-medium-border-radius);
      overflow: visible;
    }

    .p-accordion-header .p-accordion-header-link {
      background-color: white;
      color: var(--black-main);
      border: none;
      border-radius: var(--semi-medium-border-radius);
      padding: var(--default-padding) var(--default-padding)
        var(--semi-big-font-size) var(--default-padding);
      font-weight: 400;

      &:focus {
        box-shadow: none;
      }
    }

    .p-accordion-header:not(.p-disabled).p-highlight .p-accordion-header-link {
      border-radius: var(--semi-medium-border-radius)
        var(--semi-medium-border-radius) 0 0;
    }

    .p-accordion-header:not(.p-disabled).p-highlight .p-accordion-header-link,
    .p-accordion-header:not(.p-disabled) .p-accordion-header-link:hover,
    .p-accordion-header:not(.p-disabled).p-highlight:hover
      .p-accordion-header-link {
      background-color: white;
      color: var(--black-main);
      border: none;
    }

    .p-accordion-toggle-icon {
      color: var(--gray-darker);
      margin-left: auto;
      order: 1;
      padding-left: var(--medium-padding);
    }

    .p-accordion-content {
      background-color: white;
      color: var(--black-main);
      border: none;
      border-radius: 0 0 var(--semi-medium-border-radius)
        var(--semi-medium-border-radius);
      padding: 0 var(--default-padding) var(--default-padding);
    }
  }
`;

const AccordionHeaderContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--semi-medium-padding);
  font-weight: 600;
  font-size: var(--medium-font-size);
  letter-spacing: 0.32px;
  color: var(--black-main);
`;

const toSentenceCase = (key: string): string => {
  const text = key.replace(/-/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const getTranslationContent = (
  sign: PhishingSign,
  language: LanguagesEnum,
): string =>
  sign.translations.find((translation) => translation.language === language)
    ?.content ?? '';

type PhishingSignsManagementTabProps = {
  signs: PhishingSign[];
  isLoading: boolean;
  onChange: (signId: string, language: LanguagesEnum, value: string) => void;
};

export const PhishingSignsManagementTab: React.FC<
  PhishingSignsManagementTabProps
> = ({ signs, isLoading, onChange }) => {
  const [activeIndex, setActiveIndex] = useState<number | number[] | undefined>(
    undefined,
  );
  const [activeLanguage, setActiveLanguage] = useState<LanguagesEnum>(
    LanguagesEnum.EN,
  );

  if (isLoading) {
    return (
      <LoadingWrapper>
        <ProgressSpinner />
      </LoadingWrapper>
    );
  }

  return (
    <StyledAccordion
      collapseIcon="pi pi-chevron-up"
      expandIcon="pi pi-chevron-down"
      activeIndex={activeIndex}
      onTabChange={(event) => setActiveIndex(event.index ?? undefined)}
    >
      {signs.map((sign) => (
        <AccordionTab
          key={sign.id}
          header={
            <AccordionHeaderContent>
              <PhishingSignIcon signKey={sign.key} />
              {toSentenceCase(sign.key)}
            </AccordionHeaderContent>
          }
        >
          <PhishingSignCard
            languages={phishingContentLanguages}
            activeLanguage={activeLanguage}
            content={getTranslationContent(sign, activeLanguage)}
            isEmpty={(language) =>
              isEmptyRichText(getTranslationContent(sign, language))
            }
            onLanguageChange={setActiveLanguage}
            onChange={(value) => onChange(sign.id, activeLanguage, value)}
          />
        </AccordionTab>
      ))}
    </StyledAccordion>
  );
};
